import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOSchema, { ArticleSchema } from '@/components/feature/SEOSchema';
import { mangaArticles } from '@/mocks/mangaData';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const article = useMemo(() => mangaArticles.find(a => a.id === articleId), [articleId]);
  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return mangaArticles
      .filter(a => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10 text-center">
          <h1 className="font-heading text-2xl text-foreground-900">文章不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEOSchema
        type="WebPage"
        pageTitle={`${article.title} - bl漫画推荐 | ${article.category}题材`}
        pageDescription={`${article.description.slice(0, 150)} - bl漫画精选推荐`}
        pageUrl={`/article/${article.id}`}
      />
      <ArticleSchema
        article={{
          title: article.title,
          description: article.description,
          imageUrl: article.coverImage,
          datePublished: article.updateDate,
          authorName: article.author,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <article className="w-full px-4 md:px-6 py-6">
            <div className="max-w-4xl mx-auto">
              {/* Article Header */}
              <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
                <div className="w-full md:w-64 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      title={`${article.title} - bl漫画推荐`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'instant' as const })}
                    >
                      {article.category}
                    </Link>
                    {article.status === '连载中' && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                        {article.status}
                      </span>
                    )}
                    {article.status === '已完结' && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">
                        已完结
                      </span>
                    )}
                  </div>

                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mt-2 mb-3">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i>
                      <span className="text-lg font-bold text-foreground-800">{article.rating}</span>
                      <span className="text-xs text-foreground-500">/ 10</span>
                    </div>
                    <span className="text-sm text-foreground-400">{article.author}</span>
                    <span className="text-sm text-foreground-400">{article.updateDate}</span>
                  </div>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {article.description}
                  </p>

                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full text-xs bg-background-200 text-foreground-600 whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-6 text-sm text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.viewCount.toLocaleString()} 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line"></i>
                      {article.commentCount.toLocaleString()} 评论
                    </span>
                  </div>
                </div>
              </div>

              {/* Article Content */}
              <div className="prose-custom bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8 mb-10">
                <h2 className="font-heading text-xl text-foreground-900 mb-4">作品介绍</h2>
                <div className="text-foreground-700 leading-relaxed text-sm space-y-4">
                  <p>
                    《{article.title}》是一部备受好评的{article.category}题材bl漫画作品，由知名漫画家{article.author}倾心创作。
                    该作品以其独特的叙事风格和细腻的情感刻画，在bl漫画爱好者中积累了极高的人气。
                  </p>
                  <p>
                    故事以{article.category === '古风' ? '古代' : article.category === '现代' ? '现代都市' : article.category === '校园' ? '青春校园' : article.category === '科幻' ? '未来科幻' : article.category === '奇幻' ? '奇幻世界' : article.category === '悬疑' ? '悬疑推理' : article.category === '虐心' ? '情感纠葛' : '甜蜜日常'}为背景，
                    通过精巧的情节设计和富有感染力的人物塑造，为读者呈现了一段{article.category === '虐心' ? '虐心催泪却令人欲罢不能的' : article.category === '甜宠' ? '甜蜜温暖治愈心灵的' : '令人沉醉其中的'}爱情故事。
                  </p>
                  <p>
                    读者评分高达{article.rating}分，充分证明了这部bl漫画的超高水准。
                    画风{article.category === '古风' ? '古典雅致，如同一幅幅精美的工笔画' : article.category === '奇幻' ? '奇幻瑰丽，想象力丰富' : '细腻优美，人物塑造生动立体'}，
                    无论是人物设计还是场景绘制都堪称一流。
                  </p>
                  <p>
                    目前该作品状态为<strong>{article.status}</strong>，已获得超过{article.viewCount.toLocaleString()}次阅读和{article.commentCount.toLocaleString()}条读者评论，
                    是值得每一位bl漫画爱好者品读的佳作。
                  </p>
                  <p>
                    如果你喜欢{article.category}题材的bl漫画，《{article.title}》绝对不容错过。
                    在bl漫画精选平台，你可以免费在线阅读这部精彩作品，感受bl漫画的独特魅力。
                  </p>
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <section aria-label="相关推荐" className="mb-10">
                  <h2 className="font-heading text-xl text-foreground-900 mb-5">相关推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {relatedArticles.map((rel) => (
                      <ArticleCard key={rel.id} article={rel} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}